import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ExternalLink, Search, Building2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ProviderList } from '../components/ProviderList';
import { ListenButton } from '../components/ListenButton';
import { api } from '../lib/api';

const HAS_PROVIDERS = ['adp_wheelchair', 'cohb', 'rgi_toronto'];

export default function Directory() {
  const [programs, setPrograms] = useState([]);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');

  useEffect(() => {
    api.get('/programs').then(({ data }) => setPrograms(data.programs));
  }, []);

  const cats = ['All', ...new Set(programs.map((p) => p.category))];
  const filtered = programs.filter((p) =>
    (cat === 'All' || p.category === cat) &&
    (!q || (p.name + p.summary).toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold" style={{fontFamily: 'Lexend'}}>Benefits Directory</h1>
      <p className="text-muted-foreground mt-2">Browse all {programs.length} verified Ontario and federal programs.</p>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search programs…" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-9" data-testid="dir-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {cats.map((c) => (
            <Button key={c} size="sm" variant={cat === c ? 'default' : 'outline'} className={cat === c ? 'bg-primary text-primary-foreground' : ''} onClick={() => setCat(c)} data-testid={`dir-cat-${c.replace(/\s+/g, '-').toLowerCase()}`}>{c}</Button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
        {filtered.map((p) => (
          <Card key={p.id} className="p-5 bg-card" data-testid={`dir-card-${p.id}`}>
            <div className="flex flex-wrap gap-1.5 mb-2">
              <Badge variant="outline">{p.government}</Badge>
              <Badge variant="outline">{p.category}</Badge>
            </div>
            <h3 className="font-bold" style={{fontFamily: 'Lexend'}}>{p.name}</h3>
            <p className="text-sm text-muted-foreground mt-1.5 line-clamp-3">{p.summary}</p>
            <ListenButton text={`${p.name}. ${p.plain_language || p.summary} ${p.estimated_amount}.`} label={p.name} testId={`listen-${p.id}`} />
            <div className="text-sm mt-3"><span className="text-muted-foreground">Est.</span> <span className="font-semibold text-primary">{p.estimated_amount}</span></div>
            <Button asChild size="sm" variant="outline" className="mt-4"><a href={p.application_url} target="_blank" rel="noopener noreferrer">Official site <ExternalLink className="w-3 h-3 ml-1" /></a></Button>
            {HAS_PROVIDERS.includes(p.id) && (
              <Accordion type="single" collapsible className="mt-3">
                <AccordionItem value="prov" className="border-t border-border">
                  <AccordionTrigger className="text-sm font-medium py-2" data-testid={`dir-providers-${p.id}`}><span className="flex items-center gap-1.5"><Building2 className="w-4 h-4 text-primary" /> Local providers &amp; referral agencies</span></AccordionTrigger>
                  <AccordionContent><ProviderList programId={p.id} /></AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
